import React, { useState, useEffect, useCallback } from "react";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import { useLocation, useNavigate } from "react-router-dom";
import FileInput from "../../components/fileInput";
import TextArea from "../../components/commonTextArea";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import { Formik } from "formik";
import { useDispatch } from "react-redux";
import { Toast, Form, Col, Row, Card } from "react-bootstrap";
import { blogSchema } from "../../schema/validationSchemas";
import { addBlog, getTags } from "../../modules/Blog/blogSlice";
import { addTag } from "../../modules/Tag/tagSlice";
import Editor from "../../components/commonEditor";
import MultiSelectDropdown from "../../components/multiSelectDropdown";

export default function BlogAddNew() {
  const navigate = useNavigate();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [imagePreview, setImagePreview] = useState(null);
  // const [videoPreview, setVideoPreview] = useState(null);
  const [tagsOptions, setTagsOptions] = useState([]);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentPage = params.get("page") || 1;
  const dispatch = useDispatch();

  const initialEventState = {
    blog_title: "",
    author: "",
    blog_image: "",
    // blog_video: "",
    blog_description: "",
    tag_ids: [],
    meta_title: "",
    meta_description: "",
    meta_keyword: "",
    video_link: ""
  };

  const handleCreateTag = async (tagName) => {
    try {
      const values = { name: tagName };
      const response = await dispatch(addTag(values));
      if (response.payload && response.payload.success) {
        return { value: response.payload.data.id, label: response.payload.data.name };
      } else {
        throw new Error(response.data.message || "Failed to create tag.");
      }
    } catch (error) {
      console.error("Error creating tag:", error);
      throw error;
    }
  };

  const fetchTags = useCallback(async () => {
    try {
      await dispatch(getTags({})).then((res) => {
        if (res.payload) {
          if (res.payload.length > 0) {
            const formattedData = res.payload.map((legalFirm) => ({
              value: legalFirm.id,
              label: legalFirm.name,
            }));
            setTagsOptions(formattedData);
          }
        } else {
          setTagsOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleModalClose = () => {
    navigate(`/blogs?page=${currentPage}`);
  };

  const handleImageChange = (file, setFieldValue) => {
    if (file) {
      setImagePreview(URL.createObjectURL(file)); // Generate preview URL
      setFieldValue("blog_image", file); // Set the file in Formik
    }
  };

  // const handleVideoChange = (file, setFieldValue) => {
  //   if (file) {
  //     setVideoPreview(URL.createObjectURL(file)); // Generate preview URL
  //     setFieldValue("blog_video", file); // Set the file in Formik
  //   }
  // };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    await dispatch(addBlog(values))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem("blog_notify_message", res.payload.message);
          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          navigate("/blogs");
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(res.payload.message || "Failed to add blog!");
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setSnackbarMessage("Failed to add blog!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>
      <Formik
        initialValues={initialEventState}
        validationSchema={blogSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          handleChange,
          handleSubmit,
          values,
          setFieldValue,
        }) => (
          <Form
            className="bg-white p-4 rounded-2 shadow-sm "
            onSubmit={handleSubmit}
            encType="multipart/form-data"
          >
            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
            <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
              <Row>
                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="blog_title" title="Blog Title" />
                  <Input
                    id="blog_title"
                    name="blog_title"
                    type="text"
                    placeholder="Enter Blog Title"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.blog_title && touched.blog_title
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.blog_title && errors?.blog_title && (
                    <ValidationFeedback title={errors?.blog_title} />
                  )}
                </Col>

                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="author" title="Author" />
                  <Input
                    id="author"
                    name="author"
                    type="text"
                    placeholder="Enter Author Name"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.author && touched.author ? "error" : "correct"
                    }`}
                  />
                  {touched.author && errors?.author && (
                    <ValidationFeedback title={errors?.author} />
                  )}
                </Col>

                <Col md={12} className="mb-4">
                  <MultiSelectDropdown
                    label="Tags"
                    name="tag_ids"
                    value={values.tag_ids}
                    onChange={(selectedOptions) => setFieldValue("tag_ids", selectedOptions)}
                    options={tagsOptions}
                    onCreateTag={handleCreateTag}
                    placeholder="Select or create tags"
                    error={errors?.tag_ids}
                    touched={touched.tag_ids}
                    module="blog"
                  />
                </Col>
              </Row>

              <div className="mb-4">
                <CommonFormLabel htmlFor="description" title="Description" />
                <Editor
                  value={values.blog_description}
                  onChange={(content) =>
                    setFieldValue("blog_description", content)
                  }
                  height="250px"
                />
                {touched.blog_description && errors?.blog_description && (
                  <ValidationFeedback title={errors?.blog_description} />
                )}
              </div>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">
                Meta Content
              </h4>
              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel htmlFor="meta_title" title="Meta Title" />
                  <Input
                    id="meta_title"
                    name="meta_title"
                    type="text"
                    placeholder="Enter Meta Title"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.meta_title && touched.meta_title
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.meta_title && errors?.meta_title && (
                    <ValidationFeedback title={errors?.meta_title} />
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel
                    htmlFor="meta_description"
                    title="Meta Description"
                  />
                  <TextArea
                    id="meta_description"
                    name="meta_description"
                    placeholder="Enter Meta Description"
                    value={values.meta_description}
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.meta_description && touched.meta_description
                        ? "error"
                        : "correct"
                    }`}
                    rows={4}
                  />
                  {touched.meta_description && errors?.meta_description && (
                    <ValidationFeedback title={errors?.meta_description} />
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel
                    htmlFor="meta_keyword"
                    title="Meta Keywords"
                  />
                  <TextArea
                    id="meta_keyword"
                    name="meta_keyword"
                    value={values.meta_keyword}
                    placeholder="Enter Meta Keywords"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.meta_keyword && touched.meta_keyword
                        ? "error"
                        : "correct"
                    }`}
                    rows={4}
                  />
                  {touched.meta_keyword && errors?.meta_keyword && (
                    <ValidationFeedback title={errors?.meta_keyword} />
                  )}
                </Col>
              </Row>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
            <h4 className="mb-4 d-flex justify-content-center">Image</h4>
              <div className="mb-4">
                <CommonFormLabel htmlFor="blog_image" title="Upload Image" />
                <FileInput
                  name="blog_image"
                  onChange={(file) => handleImageChange(file, setFieldValue)}
                  error={errors?.blog_image}
                  touched={touched.blog_image}
                  inputType="image"
                />
                {touched.blog_image && errors?.blog_image && (
                  <ValidationFeedback title={errors?.blog_image} />
                )}
                {imagePreview && (
                  <div className="mt-3">
                    <img
                      src={imagePreview}
                      alt="Blog Preview"
                      style={{
                        marginTop: "10px",
                        width: "200px",
                        height: "auto",
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                      }}
                    />
                  </div>
                )}
              </div>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
            <h4 className="mb-4 d-flex justify-content-center">Video</h4>
            <Row>
              <Col md={6} className="mb-4">
                <CommonFormLabel htmlFor="video_link" title="Video Link (optional)" />
                <Input
                  id="video_link"
                  name="video_link"
                  type="text"
                  placeholder="Enter Video Link"
                  onChange={handleChange}
                  className={`shadow-none ${
                    errors?.video_link && touched.video_link
                      ? "error"
                      : "correct"
                  }`}
                />
                {touched.video_link && errors?.video_link && (
                  <ValidationFeedback title={errors?.video_link} />
                )}
              </Col>
            </Row>
            
              {/* <div className="mb-4">
                <CommonFormLabel htmlFor="blog_video" title="Upload Video (optional)" />
                <FileInput
                  name="blog_video"
                  onChange={(file) => handleVideoChange(file, setFieldValue)}
                  error={errors?.blog_video}
                  touched={touched.blog_video}
                  inputType="image"
                />
                {touched.blog_video && errors?.blog_video && (
                  <ValidationFeedback title={errors?.blog_video} />
                )}
                {videoPreview && (
                  <div className="mt-3">
                    <video
                      controls
                      src={videoPreview}
                      style={{
                        marginTop: "10px",
                        width: "350px",
                        height: "auto",
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}
              </div> */}
            </Card>

            <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
              <CommonButton
                title={isSubmitting ? "Adding..." : "Add"}
                type="submit"
                disabled={isSubmitting}
                className="btn-primary primary-shadow"
              />
              <CommonButton
                title="Cancel"
                onClick={handleModalClose}
                className="btn-muted"
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
